import { computed, DestroyRef, inject, signal, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataAccessCorporateDataService } from '@h5s/data-access-corporate-data';
import { DataAccessOwnerService } from '@h5s/data-access-owner';
import { Owner, OwnerCorporateData, OwnerPlanner } from '@h5s/entities';
import {
  CreateCorporateDataEvent,
  DeleteCorporateDataEvent,
  UpdateCorporateDataEvent,
} from '@h5s/feature-corporate-data';
import { FeatureUserStore } from '@h5s/feature-user';
import { EventBusService } from '@h5s/util-event';
import { LoggerService } from '@h5s/util-logger';
import { catchError, forkJoin, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { InvoiceSettings } from './models/invoice-settings';

@Service()
export class FeatureOwnerService {
  /** Services */
  private readonly logger = inject(LoggerService).getLogger('FeatureOwnerService');
  private readonly dataAccessOwnerService = inject(DataAccessOwnerService);
  private readonly dataAccessCorporateDataService = inject(DataAccessCorporateDataService);
  private readonly featureUserStore = inject(FeatureUserStore);
  private readonly eventBusService = inject(EventBusService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _owner = signal<Owner>(new Owner());
  private readonly _ownerPlanner = signal<OwnerPlanner | null>(null);
  private readonly _allOwnerCorporateData = signal<OwnerCorporateData[]>([]);

  public readonly ownerSettings = computed<Owner>(() => this._owner());
  public readonly ownerPlannerSettings = computed<OwnerPlanner | null>(() => this._ownerPlanner());
  public readonly allOwnerCorporateData = computed<OwnerCorporateData[]>(() => this._allOwnerCorporateData() ?? []);
  public readonly allActiveOwnerCorporateData = computed<OwnerCorporateData[]>(() =>
    this._allOwnerCorporateData().filter((data) => data.available),
  );
  public readonly ownerInvoiceSettings = computed<InvoiceSettings>(() => this.getInvoiceSettings(this._owner()));

  constructor() {
    // Le anagrafiche fiscali sono in cache nel servizio: ogni modifica le ricarichiamo
    this.eventBusService
      .ofTypes([CreateCorporateDataEvent, UpdateCorporateDataEvent, DeleteCorporateDataEvent])
      .pipe(
        // catchError interno: un errore di ricarica non deve chiudere lo stream di invalidazione
        switchMap(() => this.loadAllOwnerCorporateData().pipe(catchError(() => of(void 0)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadOwner(): Observable<void> {
    if (!this.featureUserStore.userAccessProperty().propertyAccessToken) {
      this.logger.error('Cannot load Owner without Property-Access-Token');
      this._owner.set(new Owner());
      return of(void 0);
    }

    return this.dataAccessOwnerService.getOwner().pipe(
      tap((ownerSettings) => {
        this._owner.set(ownerSettings);
        this.logger.log('Owner loaded successfully:', ownerSettings.corpName);
      }),
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Error loading Owner', error);
        this._owner.set(new Owner());
        return throwError(() => error);
      }),
    );
  }

  public loadAllOwnerCorporateData(): Observable<void> {
    if (!this.featureUserStore.userAccessProperty().propertyAccessToken) {
      this.logger.error('Cannot load OwnerCorporateData without Property-Access-Token');
      this._allOwnerCorporateData.set([]);
      return of(void 0);
    }

    return this.dataAccessCorporateDataService.getAllOwnerCorporateData().pipe(
      tap((allOwnerCorporateData) => {
        this._allOwnerCorporateData.set(allOwnerCorporateData);
        this.logger.log('OwnerCorporateData loaded successfully');
      }),
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Error loading OwnerCorporateData', error);
        this._allOwnerCorporateData.set([]);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Metodo utilizzato per caricare o ricaricare le impostazioni dell'Owner
   * Viene chiamato dopo che l'utente ha selezionato una struttura
   */
  public loadOwnerSettings(): Observable<void> {
    return forkJoin([this.loadOwner(), this.loadAllOwnerCorporateData()]).pipe(map(() => void 0));
  }

  public loadPlannerSettings(): Promise<void> {
    if (!this.featureUserStore.userAccessProperty().propertyAccessToken) {
      this.logger.error('Cannot load OwnerPlannerSettings without Property-Access-Token');
      this._ownerPlanner.set(null);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.dataAccessOwnerService
        .getOwnerPlanner()
        .pipe(take(1))
        .subscribe({
          next: (ownerPlanner) => {
            this._ownerPlanner.set(ownerPlanner);
            this.logger.log('OwnerPlannerSettings loaded successfully.');
            resolve();
          },
          error: (error) => {
            this.logger.error('Error loading OwnerPlannerSettings', error);
            this._ownerPlanner.set(null);
            reject(error);
          },
        });
    });
  }

  /**
   * Resetta le impostazioni dell'Owner
   * Da chiamare quando l'utente cambia struttura
   */
  public resetOwnerSettings(): void {
    this.logger.log('Reset OwnerSettings to initial state');
    this._owner.set(new Owner());
    this._ownerPlanner.set(null);
    this._allOwnerCorporateData.set([]);
  }

  /**
   * Ottiene le impostazioni che servono ad Invoice
   *
   * @returns {InvoiceSettings} Un'istanza InvoiceSettings con le impostazioni di fatturazione.
   */
  private getInvoiceSettings(owner: Owner): InvoiceSettings {
    return {
      invoiceExtraDisplayMode: owner.invoiceExtraDisplayMode,
      invoiceRoomDisplayMode: owner.invoiceRoomDisplayMode,
      showIdOnInvoice: owner.showIdOnInvoice,
      generateInvoiceAsDefault: owner.generateInvoiceAsDefault,
      showCustomersOnInvoice: owner.showCustomersOnInvoice,
      showMultilanguageInvoice: owner.showMultilanguageInvoice,
      defaultVATRate: owner.defaultVATRate,
      limitInvoiceProgressiveAndDate: owner.limitInvoiceProgressiveAndDate,
      paymentMandatoryOnInvoice: owner.paymentMandatoryOnInvoice,
    };
  }
}
